import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { ProductDetail as ProductDetailType, CalculationTrace } from '@/lib/api';
import { formatRecommendedQty } from '@/lib/recommendation';
import SeverityBadge from "@/components/ui/SeverityBadge.tsx";
import RecentSpikesTooltip from "@/components/ui/RecentSpikesTooltip.tsx";
import CalculationTraceSection from "@/components/ui/CalculationTraceSection.tsx";
import {
  ArrowLeft,
  Loader2,
  Save,
  Package,
  TrendingUp,
  Truck,
  AlertCircle,
  Layers,
  ShoppingBag,
  Calculator,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

function formatCurrency(amount: number | null) {
  if (amount === null) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'gerade eben';
  if (diffMinutes < 60) return `vor ${diffMinutes} Min.`;
  if (diffHours < 24) {
    const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `heute ${timeStr}`;
  }
  if (diffDays === 1) return 'gestern';
  return `vor ${diffDays} Tagen`;
}

export default function ProductDetail() {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [excludeSaving, setExcludeSaving] = useState(false);
  const [excludeMessage, setExcludeMessage] = useState<string>('');
  const [overrides, setOverrides] = useState({
    lead_time_override: '' as string,
    moq_override: '' as string,
    pack_size_override: '' as string
  });
  const [trace, setTrace] = useState<CalculationTrace | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceState, setTraceState] = useState<'idle' | 'loading' | 'loaded' | 'unavailable'>('idle');

  useEffect(() => {
    if (!sku) return;
    api.getProductDetail(sku).then((d) => {
      setData(d);
      setOverrides({
        lead_time_override: d.product.leadTimeOverride?.toString() ?? '',
        moq_override: d.product.moqOverride?.toString() ?? '',
        pack_size_override: d.product.packSizeOverride?.toString() ?? ''
      });
      setLoading(false);
    }).catch((error) => {
      setLoading(false);
      console.error('Error fetching product detail:', error);
      alert('Produkt konnte nicht geladen werden. Bitte Meldung in der Konsole beachten.');
    });
  }, [sku]);

  const handleSave = async () => {
    if (!sku) return;
    setSaving(true);
    try {
      await api.updateProductOverrides(sku, {
        lead_time_override: overrides.lead_time_override ? parseInt(overrides.lead_time_override) : null,
        moq_override: overrides.moq_override ? parseInt(overrides.moq_override) : null,
        pack_size_override: overrides.pack_size_override ? parseInt(overrides.pack_size_override) : null
      });
      // Reload
      const d = await api.getProductDetail(sku);
      setData(d);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };


  const handleTraceToggle = async () => {
    const next = !traceOpen;
    setTraceOpen(next);
    if (!next || traceState === 'loaded' || traceState === 'loading' || !sku) return;

    setTraceState('loading');
    try {
      const t = await api.getCalculationTrace(sku);
      setTrace(t);
      setTraceState('loaded');
    } catch {
      setTraceState('unavailable');
    }
  };

  const handlePlanningExclusionToggle = async (isExcludedFromPlanning: boolean) => {
    if (!sku || !data) return;

    setExcludeSaving(true);
    setExcludeMessage('');
    try {
      await api.updateProductOverrides(sku, { isExcludedFromPlanning });
      const d = await api.getProductDetail(sku);
      setData(d);
      setExcludeMessage('Status wurde gespeichert.');
    } catch (err) {
      console.error('Toggle failed:', err);
      setExcludeMessage('Fehler beim Speichern des Status.');
    } finally {
      setExcludeSaving(false);
    }
  };

  const handleParentOnlyToggle = async (trackedViaParentOnly: boolean) => {
    if (!sku || !data) return;

    setExcludeSaving(true);
    setExcludeMessage('');
    try {
      await api.updateProductOverrides(sku, { trackedViaParentOnly });
      const d = await api.getProductDetail(sku);
      setData(d);
      setExcludeMessage('Status wurde gespeichert.');
    } catch (err) {
      console.error('Toggle failed:', err);
      setExcludeMessage('Fehler beim Speichern des Status.');
    } finally {
      setExcludeSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-ink-soft mx-auto mb-4" />
        <p className="text-ink-muted">Produkt nicht gefunden</p>
        <button onClick={() => navigate('/')} className="btn-secondary mt-4">Zurück zum Dashboard</button>
      </div>
    );
  }

  const { product, metric, kapitalbindung, salesHistory, stockHistory, openPurchases, components, packagingUnits, lastSyncAt } = data;

  const recommendedQtyDisplay = metric
    ? formatRecommendedQty(metric.recommendedOrderQty, metric.severity)
    : null;

  const salesChartData = salesHistory.map((s) => ({
    date: new Date(s.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
    // Sale quantities are fractional (sub-unit sales, BOM factors); parseInt would
    // truncate 2.5 -> 2 and anything below 1 -> 0, re-hiding the very sales the
    // float-qty change preserves. Round to 4 decimals to drop binary float drift
    // (e.g. 0.1 summed → 0.7000000000000001) while keeping real precision.
    qty: Math.round(Number(s.qty) * 10000) / 10000,
  }));

  const stockChartData = stockHistory.map((s) => ({
    date: new Date(s.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
    bestand: s.onHand,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/')}
          className="mt-1 p-2 rounded-panel hover:bg-surface-2 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-ink-muted" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            {metric && <SeverityBadge severity={metric.severity} />}
            {product.isExcludedFromPlanning && (
              <span className="inline-flex items-center rounded-full bg-surface-2 border border-line px-3 py-1 text-xs font-medium text-ink-muted">
                Von Planung ausgeschlossen
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">{product.name}</h1>
            {/*{metric && <SeverityBadge severity={metric.severity} />}*/}
            <p className="font-mono text-[10.5px] text-ink-soft mt-1">({product.sku})</p>
          </div>
          {product.supplier && (
            <p className="text-sm text-ink-muted mt-1">{product.supplier.name}</p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {metric && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="rounded-card border border-line bg-surface p-4">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Tagesverbrauch</p>
              {metric.recentSpikes && metric.recentSpikes.length > 0 && (
                <RecentSpikesTooltip spikes={metric.recentSpikes} />
              )}
            </div>
            <p className="text-xl font-extrabold tracking-tight text-ink">{metric.dailyUsage.toFixed(1)}</p>
          </div>
          <div className="rounded-card border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Reichweite</p>
            <p className="text-xl font-extrabold tracking-tight text-ink">{metric.coverageDays.toFixed(0)} Tage</p>
          </div>
          <div className="rounded-card border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Bestand</p>
            <p className="text-xl font-extrabold tracking-tight text-ink">{metric.onHand.toLocaleString('de-DE')}</p>
          </div>
          <div className="rounded-card border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Reserviert</p>
            <p className="text-xl font-extrabold tracking-tight text-risk-yellow">
              {metric.reserved > 0 ? metric.reserved.toLocaleString('de-DE') : '—'}
            </p>
          </div>
          <div className="rounded-card border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Empf. Bestellmenge</p>
            <p className="text-xl font-extrabold tracking-tight text-brand" title={recommendedQtyDisplay?.title}>
              {recommendedQtyDisplay?.text}
            </p>
          </div>
          <div className="rounded-card border border-line bg-surface p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft mb-1">Kapitalbindung</p>
            <p className="text-xl font-extrabold tracking-tight text-ink">{formatCurrency(kapitalbindung)}</p>
          </div>
        </div>
      )}

      {/* Last Sync */}
      {lastSyncAt && (
        <p className="font-mono text-[10.5px] text-ink-soft">
          Zuletzt synchronisiert: {formatRelativeTime(lastSyncAt)}
        </p>
      )}

      {/* Calculation Trace */}
      {metric && (
        <div className="rounded-card border border-line bg-surface">
          <button
            onClick={handleTraceToggle}
            className="w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-surface-2 transition-colors rounded-card"
          >
            {traceOpen ? (
              <ChevronDown className="h-4 w-4 text-ink-soft" />
            ) : (
              <ChevronRight className="h-4 w-4 text-ink-soft" />
            )}
            <Calculator className="h-4 w-4 text-ink-muted" />
            <span className="text-sm font-semibold text-ink">Berechnung</span>
            <span className="text-xs text-ink-soft ml-1">So kommen die Zahlen zustande</span>
          </button>

          {traceOpen && (
            <div className="px-5 pb-5">
              {traceState === 'loading' && (
                <div className="flex items-center gap-2 text-sm text-ink-muted py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Lade Berechnung …
                </div>
              )}
              {traceState === 'unavailable' && (
                <p className="text-sm text-ink-muted py-4">
                  Keine Berechnung verfügbar. Sie entsteht beim nächsten Berechnungslauf.
                </p>
              )}
              {traceState === 'loaded' && trace && (
                <div className="space-y-4">
                  {trace.trace.frozen && (
                    <p className="text-xs text-ink-muted bg-surface-2 border border-line rounded-panel px-3 py-2">
                      Artikel ist ausverkauft. Verbrauch, Bestellmenge und Risiko sind aus dem letzten
                      Lauf mit Bestand eingefroren, damit die Empfehlung nicht auf Null fällt.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-ink-soft">
                    {Object.entries(trace.trace.params).map(([key, value]) => (
                      <span key={key} className="font-mono">
                        {key}: {Array.isArray(value) ? value.join('/') : String(value)}
                      </span>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {([
                      ['dailyUsage', 'Tagesverbrauch'],
                      ['coverageDays', 'Reichweite'],
                      ['severity', 'Status (Ampel)'],
                      ['recommendedOrderQty', 'Empfohlene Bestellmenge'],
                      ['riskValue', 'Umsatzrisiko'],
                      ['orderByDate', 'Spätester Bestellzeitpunkt'],
                    ] as Array<[string, string]>).map(([key, title]) =>
                      trace.trace.sections[key] ? (
                        <CalculationTraceSection key={key} title={title} section={trace.trace.sections[key]} />
                      ) : null
                    )}
                  </div>
                  <p className="font-mono text-[10px] text-ink-soft">
                    Berechnet: {new Date(trace.computedAt).toLocaleString('de-DE')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="rounded-card border border-line bg-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-brand" />
            <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink">Verbrauch (lookbackDays Tage)</h3>
          </div>
          {salesChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={salesChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE1D4" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A89E8E' }} />
                <YAxis tick={{ fontSize: 11, fill: '#A89E8E' }} />
                <Tooltip
                  contentStyle={{
                    background: '#FFFFFF',
                    border: '1px solid #EAE1D4',
                    borderRadius: 12,
                    fontFamily: 'Hanken Grotesk',
                  }}
                />
                <Bar dataKey="qty" fill="#00D68F" radius={[4, 4, 0, 0]} name="Menge" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-ink-soft text-sm">
              Keine Verkaufsdaten vorhanden
            </div>
          )}
        </div>

        {/* Stock Chart */}
        <div className="rounded-card border border-line bg-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5 text-brand" />
            <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink">Bestandsverlauf</h3>
          </div>
          {stockChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={stockChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE1D4" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#A89E8E' }} />
                <YAxis tick={{ fontSize: 11, fill: '#A89E8E' }} />
                <Tooltip
                  contentStyle={{
                    background: '#FFFFFF',
                    border: '1px solid #EAE1D4',
                    borderRadius: 12,
                    fontFamily: 'Hanken Grotesk',
                  }}
                />
                <Line type="monotone" dataKey="bestand" stroke="#00D68F" strokeWidth={2} dot={false} name="Bestand" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-ink-soft text-sm">
              Keine Bestandsdaten vorhanden
            </div>
          )}
        </div>
      </div>

      {/* Production Components */}
      {components.length > 0 && (
        <div className="rounded-card border border-line bg-surface overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand" />
            <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink">Produktionskomponenten</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Artikel</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Status</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Reichweite</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Faktor</th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp) => (
                <tr
                  key={comp.sku}
                  className="border-b border-surface-3 hover:bg-surface-2 cursor-pointer transition-colors"
                  onClick={() => navigate(`/products/${encodeURIComponent(comp.sku)}`)}
                >
                  <td className="px-5 py-3">
                    <span className="text-[13.5px] font-bold text-ink">{comp.name ?? comp.sku}</span>
                    <span className="ml-2 font-mono text-[10.5px] text-ink-soft">{comp.sku}</span>
                  </td>
                  <td className="px-5 py-3">
                    {comp.severity ? <SeverityBadge severity={comp.severity} /> : <span className="text-ink-soft text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                    {comp.coverageDays !== null ? `${comp.coverageDays.toFixed(0)} Tage` : '—'}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                    {comp.factorQty.toLocaleString('de-DE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Packaging Units / Verkaufseinheiten */}
      {packagingUnits.length > 0 && (
        <div className="rounded-card border border-line bg-surface overflow-hidden">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-brand" />
            <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink">Verkaufseinheiten</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Artikel</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Umrechnung</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Ø VE/Tag</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Ø Stk./Tag</th>
              </tr>
            </thead>
            <tbody>
              {packagingUnits.map((ve) => (
                <tr key={ve.veSku} className="border-b border-surface-3 hover:bg-surface-2 transition-colors">
                  <td className="px-5 py-3">
                    <span className="text-[13.5px] font-bold text-ink">{ve.name ?? ve.veSku}</span>
                    <span className="ml-2 font-mono text-[10.5px] text-ink-soft">{ve.veSku}</span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[12.5px] text-ink-muted">
                    1 VE = {ve.quantity.toLocaleString('de-DE')} Stk.
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                    {ve.avgDailyUsageVe.toLocaleString('de-DE', { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                    {ve.avgDailyUsageBase.toLocaleString('de-DE', { maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Open Purchases */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <Truck className="h-5 w-5 text-brand" />
          <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink">Offene Bestellungen</h3>
        </div>
        {openPurchases.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Bestellnr.</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Offene Menge</th>
                <th className="px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">ETA</th>
              </tr>
            </thead>
            <tbody>
              {openPurchases.map((po, i) => (
                <tr key={i} className="border-b border-surface-3 hover:bg-surface-2 transition-colors">
                  <td className="px-5 py-3 font-mono text-[10.5px] text-ink-muted">{po.poNumber}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">{po.qtyOpen.toLocaleString('de-DE')}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                    {po.etaDate ? (
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        {po.etaDate < new Date().toISOString().slice(0, 10) && (
                          <span
                            className="inline-flex items-center rounded-full border border-risk-red/40 bg-risk-red-soft px-1.5 py-0.5 text-[9px] font-sans font-medium text-risk-red"
                            title="Geplantes Lieferdatum überschritten, Ware nicht eingegangen - Bestellung in weclapp prüfen"
                          >
                            Überfällig
                          </span>
                        )}
                        {po.ignoredReason === 'age' && (
                          <span
                            className="inline-flex items-center rounded-full border border-line bg-surface-2 px-1.5 py-0.5 text-[9px] font-sans font-medium text-ink-soft"
                            title="Liefertermin liegt außerhalb der Zulauf-Altersgrenze - Position zählt nicht mehr in die Bestellempfehlung"
                          >
                            Nicht verrechnet (zu alt)
                          </span>
                        )}
                        <span className={po.etaDate < new Date().toISOString().slice(0, 10) ? 'text-risk-red' : undefined}>
                          {new Date(po.etaDate).toLocaleDateString('de-DE')}
                        </span>
                      </span>
                    ) : (
                      <span className="text-risk-yellow">Kein ETA</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-5 py-4 text-sm text-ink-soft">Keine offenen Bestellungen</p>
        )}
      </div>

      <div className="rounded-card border border-line bg-surface p-5">
        <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink mb-2">Von Planung ausschließen</h3>
        <p className="text-sm text-ink-muted mb-4">
          Ausgeschlossene Artikel werden im Dashboard, in KPIs, OOS Risiken, Alerts und in der Bestellplanung nicht berücksichtigt.
        </p>
        <label className="inline-flex items-center gap-3">
          <input
            type="checkbox"
            checked={product.isExcludedFromPlanning}
            disabled={excludeSaving}
            onChange={(e) => handlePlanningExclusionToggle(e.target.checked)}
          />
          <span className="text-sm text-ink-muted">Von Planung ausschließen</span>
          {excludeSaving && <Loader2 className="h-4 w-4 animate-spin text-brand" />}
        </label>
        {excludeMessage && <p className="text-xs text-ink-muted mt-3">{excludeMessage}</p>}
      </div>

      <div className="rounded-card border border-line bg-surface p-5">
        <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink mb-2">Nur am Hauptartikel tracken</h3>
        <p className="text-sm text-ink-muted mb-4">
          Der Artikel bekommt keine eigene Zeile in den Lagerrisiken, erscheint aber weiterhin unterhalb seiner
          Hauptartikel. Ampel-Vererbung und abgeleitete Bestellmengen bleiben aktiv. Wirkt nur bei Artikeln,
          die als Stücklisten-Komponente vorkommen - sonst bleibt die eigene Zeile erhalten.
        </p>
        <label className="inline-flex items-center gap-3">
          <input
            type="checkbox"
            checked={product.trackedViaParentOnly}
            disabled={excludeSaving}
            onChange={(e) => handleParentOnlyToggle(e.target.checked)}
          />
          <span className="text-sm text-ink-muted">Nur am Hauptartikel tracken</span>
          {excludeSaving && <Loader2 className="h-4 w-4 animate-spin text-brand" />}
        </label>
      </div>

      {/* Parameter Overrides */}
      <div className="rounded-card border border-line bg-surface p-5">
        <h3 className="text-[16.5px] font-extrabold tracking-tight text-ink mb-4">Parameter-Override</h3>
        <p className="text-sm text-ink-muted mb-4">
          Überschreiben Sie die Standard-Parameter für dieses Produkt. Leere Felder verwenden die globalen Einstellungen.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-1">
              Lead Time (Tage)
              {data.parameters && (
                <span className="text-ink-soft font-normal"> · Standard: {data.parameters.default_lead_time_days}</span>
              )}
            </label>
            <input
              type="number"
              value={overrides.lead_time_override}
              onChange={(e) => setOverrides((p) => ({ ...p, lead_time_override: e.target.value }))}
              className="input-field"
              placeholder="Standard verwenden"
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-1">MOQ (Mindestbestellmenge)</label>
            <input
              type="number"
              value={overrides.moq_override}
              onChange={(e) => setOverrides((p) => ({ ...p, moq_override: e.target.value }))}
              className="input-field"
              placeholder="Keine MOQ"
              min={1}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-1">VE-Größe (Bestellvielfaches)</label>
            <input
              type="number"
              value={overrides.pack_size_override}
              onChange={(e) => setOverrides((p) => ({ ...p, pack_size_override: e.target.value }))}
              className="input-field"
              placeholder="Keine Rundung"
              min={1}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
